import type { Pead, ScreenerData } from "epsm-api-types/models";
import { memo } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Timeframes } from "@/components/widgets/Chart/types";
import type { FinancialWithPeriod } from "@/components/widgets/Financials/types";
import { BalanceSheetChart } from "@/components/widgets/Financials/ui/components/BalanceSheetChart";
import { IncomeStatementChart } from "@/components/widgets/Financials/ui/components/IncomeStatementChart";
import { PercentOfRevenuesChart } from "@/components/widgets/Financials/ui/components/PercentOfRevenuesChart";
import type { QuoteExpandedCharts } from "../../../type";
import { Competitors } from "../Competitors";
import { EarningsTable } from "../EarningsTable";
import { EpsmTable } from "../EpsmTable";
import { KeyDataPointsTable } from "../KeyDataPointsTable";
import { LineHeader } from "../LineHeader";
import { PriceAction } from "../PriceAction";
import { PriceDriftTable } from "../PriceDriftTable";
import { QuoteChart } from "../QuoteChart";
import { QuoteNews } from "../QuoteNews";
import { QuotePriceHeader } from "../QuotePriceHeader";
import { StreetView } from "../StreetView";

export const AdvancedQuote = memo(
	({
		ticker,
		tileId,
		screenerData,
		financialsData,
		peadData,
		topCompetitorsScreenerData,
		expandedCharts,
		onStateChange,
	}: {
		ticker: string;
		tileId: string;
		screenerData: ScreenerData | null;
		financialsData: FinancialWithPeriod[];
		peadData: Pead | null;
		topCompetitorsScreenerData: ScreenerData[] | null;
		expandedCharts?: QuoteExpandedCharts;
		onStateChange?: OnStateChange;
	}) => {
		return (
			<div className={"flex flex-wrap gap-5 m-3"} data-testid="advanced-quote">
				<QuotePriceHeader screenerData={screenerData} />

				<EpsmTable screenerData={screenerData} />
				<QuoteChart
					ticker={ticker}
					isCollapsible={true}
					expandedCharts={expandedCharts}
					onStateChange={onStateChange}
				/>
				<QuoteChart
					ticker={ticker}
					timeFrame={Timeframes.FIVE_MIN}
					isCollapsible={true}
					expandedCharts={expandedCharts}
					onStateChange={onStateChange}
				/>
				<EarningsTable screenerData={screenerData} />
				<KeyDataPointsTable screenerData={screenerData} />
				<Competitors
					topCompetitorsScreenerData={topCompetitorsScreenerData}
					tileId={tileId}
				/>
				<StreetView data={screenerData} />
				<PriceDriftTable screenerData={screenerData} peadData={peadData} />
				<PriceAction data={screenerData} />
				<QuoteNews ticker={ticker} tileId={tileId} />
				<PercentOfRevenuesChart
					data={financialsData}
					header={<LineHeader title="% of Revenues" />}
				/>
				<IncomeStatementChart
					data={financialsData}
					header={<LineHeader title="Income Statement" />}
				/>
				<BalanceSheetChart
					data={financialsData}
					header={<LineHeader title="Balance Sheet" />}
				/>
			</div>
		);
	},
);
